/**
 * Platform-operator routes. Only `apps/admin` uses these.
 *
 * `require_platform_admin` answers **404** rather than 403 to everyone else, which
 * is why `isPlatformAdmin()` below treats a 404 as a clean "no" instead of an error:
 * the panel is meant to be invisible, not merely forbidden.
 *
 * This is the whole platform surface the API has today — schools, licences and
 * platform admins. Revenue, user counts, AI spend and question-bank health have no
 * routes, so `apps/admin` does not show them. Adding them means adding routes to
 * modulus-backend, not inventing numbers here.
 */

import type { ApiClient } from '../core/client'
import { ApiError } from '../core/client'
import type { UUID } from '../types/common'
import type {
  ManualLicenseRequest,
  PlatformAdmin,
  SchoolAdminSummary,
  SchoolCreateRequest,
} from '../types/school'

export class PlatformResource {
  constructor(private readonly client: ApiClient) {}

  schools(signal?: AbortSignal): Promise<SchoolAdminSummary[]> {
    return this.client.get<SchoolAdminSummary[]>('/admin/schools', undefined, signal)
  }

  school(schoolId: UUID, signal?: AbortSignal): Promise<SchoolAdminSummary> {
    return this.client.get<SchoolAdminSummary>(`/admin/schools/${schoolId}`, undefined, signal)
  }

  /** Creates the school and invites its admins in one call. Each becomes an
   *  `admin` member the first time they log in, with no further action from us.
   *  More than one can be seeded here — a school launched with a single admin
   *  loses its only key-holder the moment that person leaves. */
  createSchool(body: SchoolCreateRequest): Promise<SchoolAdminSummary> {
    return this.client.post<SchoolAdminSummary>('/admin/schools', body)
  }

  /** A sales-led, invoice-billed licence — how a school is onboarded before Stripe
   *  is involved at all. */
  grantLicense(schoolId: UUID, body: ManualLicenseRequest): Promise<SchoolAdminSummary> {
    return this.client.post<SchoolAdminSummary>(`/admin/schools/${schoolId}/license`, body)
  }

  platformAdmins(signal?: AbortSignal): Promise<PlatformAdmin[]> {
    return this.client.get<PlatformAdmin[]>('/admin/platform-admins', undefined, signal)
  }

  /** Grants the platform role. The very first row is seeded by a script in the
   *  backend repo — this cannot bootstrap itself. */
  grantPlatformAdmin(email: string): Promise<PlatformAdmin> {
    return this.client.post<PlatformAdmin>('/admin/platform-admins', { email })
  }

  /**
   * Whether the signed-in account is a platform admin.
   *
   * There is no "am I?" route, so this probes the cheapest gated one. A 404 is the
   * gate's own answer for "not you" and means false; anything else is a real failure
   * and is rethrown, so an outage is never silently reported as "no access".
   */
  async isPlatformAdmin(signal?: AbortSignal): Promise<boolean> {
    try {
      await this.platformAdmins(signal)
      return true
    } catch (error) {
      if (error instanceof ApiError && (error.isNotFound || error.isForbidden)) return false
      throw error
    }
  }
}
